import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { cx } from '@/utils/cx';
import styles from '@/styles/components/TimeInput/style.scss';

type Period = 'AM' | 'PM';

interface TimeInputProps {
  label: string;
  /** "HH:MM" in 24-hour time (what the forms work in), or "" when empty. */
  value: string;
  /** Called with "HH:MM" (24-hour) once hour and minute are both valid, or "" while they aren't. */
  onChange: (value: string) => void;
  hint?: string;
  required?: boolean;
}

interface Parts {
  hour: string;
  minute: string;
  period: Period;
}

const pad = (n: number) => String(n).padStart(2, '0');
const digits = (v: string) => v.replace(/\D/g, '').slice(0, 2);

/** "14:05" → 2 · 05 · PM. Empty: blank fields, with the current half of the day preselected. */
function toParts(value: string): Parts {
  const [h, m] = value ? value.split(':').map(Number) : [NaN, NaN];
  if (!Number.isFinite(h) || !Number.isFinite(m)) {
    return { hour: '', minute: '', period: new Date().getHours() < 12 ? 'AM' : 'PM' };
  }
  return { hour: String(h! % 12 || 12), minute: pad(m!), period: h! < 12 ? 'AM' : 'PM' };
}

/** 2 · 05 · PM → "14:05"; "" unless the hour is 1–12 and the minute 0–59. */
function toValue({ hour, minute, period }: Parts): string {
  const h = Number(hour);
  const m = Number(minute);
  if (!hour || !minute || !(h >= 1 && h <= 12) || !(m >= 0 && m <= 59)) return '';
  return `${pad((h % 12) + (period === 'PM' ? 12 : 0))}:${pad(m)}`;
}

/**
 * A time in 12-hour form (hour · minute · AM/PM), whatever the computer's clock setting: the browser's own time
 * input follows the system locale and can't be told to use 12 hours. Typing two hour digits (or one from 2 to 9)
 * moves on to the minutes; ↑/↓ step the hour or minute; A or P sets the half of the day.
 */
export function TimeInput({ label, value, onChange, hint, required = true }: TimeInputProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const [parts, setParts] = useState(() => toParts(value));
  // The latest parts, read by every handler. Moving focus to the minutes inside the hour's onChange fires the
  // hour's onBlur at once, before React re-renders, so `parts` there would still be the hour without its new digit
  // (typing 11 gave 1, and 09 gave 1).
  const latest = useRef(parts);
  const minuteRef = useRef<HTMLInputElement>(null);
  // The last value sent up, so a change from outside (not our own echo) resets the fields.
  const sent = useRef(value);

  useEffect(() => {
    if (value !== sent.current) {
      sent.current = value;
      latest.current = toParts(value);
      setParts(latest.current);
    }
  }, [value]);

  const update = (patch: Partial<Parts>) => {
    const next = { ...latest.current, ...patch };
    latest.current = next;
    setParts(next);
    const v = toValue(next);
    if (v !== sent.current) {
      sent.current = v;
      onChange(v);
    }
  };

  const step = (field: 'hour' | 'minute', by: number) => {
    if (field === 'hour') {
      const h = Number(latest.current.hour) || (by > 0 ? 12 : 1);
      update({ hour: String(((h - 1 + by + 12) % 12) + 1) });
    } else {
      const m = Number(latest.current.minute) || 0;
      update({ minute: pad((m + by + 60) % 60) });
    }
  };

  const onKeyDown = (field: 'hour' | 'minute') => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      step(field, e.key === 'ArrowUp' ? 1 : -1);
    } else if (e.key === 'a' || e.key === 'A' || e.key === 'p' || e.key === 'P') {
      e.preventDefault();
      update({ period: e.key.toUpperCase() === 'A' ? 'AM' : 'PM' });
    } else if (field === 'minute' && e.key === 'Backspace' && !latest.current.minute) {
      // Backspace from an empty minute goes back to the hour.
      e.currentTarget.parentElement?.querySelector<HTMLInputElement>('input')?.focus();
    }
  };

  /** On leaving the hour: 0 → 1, 13+ → 12, "09" → "9". */
  const tidyHour = () => {
    const { hour } = latest.current;
    if (hour) update({ hour: String(Math.min(12, Math.max(1, Number(hour)))) });
  };

  /** On leaving the minute: "5" → "05", 60+ → 59. */
  const tidyMinute = () => {
    const { minute } = latest.current;
    if (minute) update({ minute: pad(Math.min(59, Number(minute))) });
  };

  return (
    <div className={styles.wrapper}>
      <span id={`${id}-label`} className={styles.label}>
        {label}
      </span>
      <div role="group" aria-labelledby={`${id}-label`} aria-describedby={hintId} className={styles.box}>
        <input
          className={styles.part}
          inputMode="numeric"
          autoComplete="off"
          placeholder="hh"
          aria-label={`${label}, hour`}
          required={required}
          value={parts.hour}
          onChange={(e) => {
            const hour = digits(e.target.value);
            update({ hour });
            // Two digits, or a first digit that can't start 10–12 (2–9): on to the minutes.
            if (hour.length === 2 || Number(hour) > 1) minuteRef.current?.focus();
          }}
          onFocus={(e) => e.target.select()}
          onBlur={tidyHour}
          onKeyDown={onKeyDown('hour')}
        />
        <span className={styles.colon} aria-hidden>
          :
        </span>
        <input
          ref={minuteRef}
          className={styles.part}
          inputMode="numeric"
          autoComplete="off"
          placeholder="mm"
          aria-label={`${label}, minutes`}
          required={required}
          value={parts.minute}
          onChange={(e) => update({ minute: digits(e.target.value) })}
          onFocus={(e) => e.target.select()}
          onBlur={tidyMinute}
          onKeyDown={onKeyDown('minute')}
        />
        <div className={styles.periods} role="group" aria-label={`${label}, AM or PM`}>
          {(['AM', 'PM'] as const).map((p) => (
            <button
              key={p}
              type="button"
              className={cx(styles.period, parts.period === p && styles.periodOn)}
              aria-pressed={parts.period === p}
              onClick={() => update({ period: p })}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
      {hint && (
        <span id={hintId} className={styles.hint}>
          {hint}
        </span>
      )}
    </div>
  );
}
